import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import { KpiService } from "../../../services/shipment/kpi.ts";
import {
  KpiShipmentPerformanceFilter,
  KpiShipmentPerformance,
} from "../../../Models/KpiShipmentPerformance";

const kpiService = new KpiService();

interface ShipmentsPerformancesFirstAttemptProviderProps {
  children: ReactNode;
  filters: KpiShipmentPerformanceFilter;
}

interface ShipmentsPerformancesFirstAttemptContextValue {
  data: KpiShipmentPerformance[];
  loader: boolean;
  filters: KpiShipmentPerformanceFilter;
}

const ShipmentsPerformancesFirstAttemptContext = createContext<
  ShipmentsPerformancesFirstAttemptContextValue | undefined
>(undefined);

const ShipmentsPerformancesFirstAttemptProvider: React.FC<
  ShipmentsPerformancesFirstAttemptProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<KpiShipmentPerformance[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentPerformance> | null>(null);

  const prevFilters = useRef<KpiShipmentPerformanceFilter>(filters);

  const getPerformancesFirstAttempt = (): void => {
    setLoader(true);
    kpiService
      .getPerformancesFirstAttempt(filters)
      .then((res) => {
        setLoader(false);
        setData(res?.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getPerformancesFirstAttempt();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getPerformancesFirstAttempt();
  }, []);

  return (
    <ShipmentsPerformancesFirstAttemptContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsPerformancesFirstAttemptContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsPerformancesFirstAttemptContext.
 * Ensures that the context is used only within a ShipmentsPerformancesFirstAttemptProvider.
 */
const useShipmentsPerformancesFirstAttemptContext =
  (): ShipmentsPerformancesFirstAttemptContextValue => {
    const context = useContext(ShipmentsPerformancesFirstAttemptContext);
    if (!context) {
      throw new Error(
        "useShipmentsPerformancesFirstAttemptContext must be used within a ShipmentsPerformancesFirstAttemptProvider"
      );
    }
    return context;
  };

// Export the provider and custom hook for use in other components
export {
  ShipmentsPerformancesFirstAttemptProvider,
  useShipmentsPerformancesFirstAttemptContext,
};
